<template>
  <div class="layout-profile" v-if="this.$store.state.auth.status.loggedIn">
    <div>
      <img src="assets/layout/images/profile.png" alt="" />
    </div>
    <button class="p-link layout-profile-link" @click="onClick">
      <span class="username">{{ currentUser.username }}</span>
      <i class="pi pi-fw pi-cog"></i>
    </button>
    <div v-if="isImpersonating" class="impersonation-profile-chip">
      <span>A impersonar: {{ currentUser.username }}</span>
      <button
        class="p-link impersonation-chip-close"
        v-tooltip.right="'Terminar impersonação'"
        @click="stopImpersonation()"
      >
        <i class="pi pi-times"></i>
      </button>
    </div>
    <transition name="layout-submenu-wrapper">
      <ul v-show="expanded">
        <li>
          <button class="p-link" @click="showProfile()">
            <i class="pi pi-fw pi-user"></i><span>Perfil</span>
          </button>
        </li>
        <li v-if="isImpersonating">
          <button class="p-link" @click="stopImpersonation()">
            <i class="pi pi-fw pi-user-minus"></i><span>Terminar impersonação</span>
          </button>
        </li>
        <li v-if="canImpersonate && !isImpersonating">
          <button class="p-link" @click="openImpersonationDialog()">
            <i class="pi pi-fw pi-user-plus"></i><span>Iniciar impersonação</span>
          </button>
        </li>
        <li>
          <button class="p-link" @click="logout()">
            <i class="pi pi-fw pi-power-off"></i><span>Logout</span>
          </button>
        </li>
      </ul>
    </transition>
    <Dialog
      :visible.sync="impersonationDialog.show"
      :style="{ width: $isMobile() ? '95vw' : '35vw' }"
      header="Impersonar utilizador"
      :modal="true"
      class="impersonation-dialog-profile"
      :contentStyle="{ overflow: 'visible' }"
    >
      <div class="p-fluid">
        <label class="p-mb-2">Selecionar utilizador ativo</label>
        <Dropdown
          v-model="impersonationDialog.userId"
          :options="impersonationDialog.users"
          optionLabel="impersonationLabel"
          optionValue="id"
          :filter="true"
          filterBy="impersonationLabel,username,name,department"
          :showClear="true"
          placeholder="Escolha um utilizador"
          :appendTo="'body'"
          panelClass="impersonation-dropdown-panel"
        />
      </div>
      <template #footer>
        <Button
          label="Cancelar"
          class="p-button-text"
          @click="closeImpersonationDialog()"
        />
        <Button
          label="Impersonar"
          icon="pi pi-user-plus"
          :disabled="!impersonationDialog.userId"
          @click="confirmStartImpersonation()"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import usersService from "./services/user.service";

export default {
  data() {
    return {
      expanded: false,
      impersonationDialog: {
        show: false,
        users: [],
        userId: null,
      },
    };
  },
  computed: {
    currentUser() {
      if (this.$store.state.auth.status.loggedIn) {
        return this.$store.state.auth.user;
      }
      return null;
    },
    isImpersonating() {
      return !!(
        this.currentUser &&
        this.currentUser.impersonation &&
        this.currentUser.impersonation.active
      );
    },
    canImpersonate() {
      return !!this.can("impersonateUsers");
    },
  },
  methods: {
    onClick(event) {
      this.expanded = !this.expanded;
      event.preventDefault();
    },
    logout() {
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    },
    showProfile() {
      this.$router.push("profile").catch(()=>{});
    },
    async stopImpersonation() {
      const user = await this.$store.dispatch("auth/stopImpersonation");
      if (!user) {
        return;
      }
      this.expanded = false;
      this.$router.push("/").catch(() => {});
    },
    async openImpersonationDialog() {
      if (!this.canImpersonate) {
        return;
      }

      if (!this.impersonationDialog.users.length) {
        const users = await usersService.activesUsers();
        this.impersonationDialog.users = this.normalizeImpersonationUsers(users);
      }

      this.expanded = false;
      this.impersonationDialog.userId = null;
      this.impersonationDialog.show = true;
    },
    closeImpersonationDialog() {
      this.impersonationDialog.show = false;
      this.impersonationDialog.userId = null;
    },
    async confirmStartImpersonation() {
      if (!this.impersonationDialog.userId) {
        return;
      }

      const selectedUserId = Number(this.impersonationDialog.userId);
      if (
        this.currentUser &&
        Number(this.currentUser.id) === selectedUserId
      ) {
        this.$toast.add({
          severity: "warn",
          summary: "Não pode impersonar o próprio utilizador",
          life: 3000,
        });
        return;
      }

      const user = await this.$store.dispatch(
        "auth/startImpersonation",
        selectedUserId
      );
      if (!user) {
        this.$toast.add({
          severity: "error",
          summary: "Erro ao iniciar impersonação",
          life: 3000,
        });
        return;
      }

      this.closeImpersonationDialog();
      this.$router.push("/").catch(() => {});
    },
    normalizeImpersonationUsers(users) {
      if (!Array.isArray(users)) {
        return [];
      }

      return users
        .filter((user) => user && user.id)
        .map((user) => {
          const username = String(user.username || "").trim();
          const name = String(user.name || "").trim();
          const department = String(user.department || "").trim();
          const labelParts = [username];

          if (name && name !== username) {
            labelParts.push(name);
          }
          if (department) {
            labelParts.push(department);
          }

          return {
            ...user,
            impersonationLabel: labelParts.join(" | "),
          };
        })
        .sort((a, b) => String(a.username).localeCompare(String(b.username)));
    },
  },
};
</script>

<style scoped>
.impersonation-dialog-profile .p-dialog-content {
  overflow: visible !important;
}

.impersonation-dropdown-panel {
  z-index: 2100 !important;
}

.impersonation-profile-chip {
  align-items: center;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 999px;
  color: #92400e;
  display: inline-flex;
  font-size: 0.8rem;
  font-weight: 600;
  gap: 0.35rem;
  margin-top: 0.4rem;
  max-width: 100%;
  padding: 0.25rem 0.65rem;
}

.impersonation-chip-close {
  color: #92400e !important;
  margin: 0 !important;
  min-width: auto !important;
  padding: 0 !important;
}
</style>
